import { firebaseShared } from "@shared/firebase.js";
import * as firebase from "@shared/firebase.js";
import * as environment from "@shared/environment.js";
import { dlog } from "@shared/utility.js";
import * as imageCache from "@shared/imageCache.js";

let allColors = [];

function colorWithID(id) {
    return allColors.find(item => item.id === id);
}

function getAllColors() {
    return allColors ? [...allColors] : [];
}

async function fetchColors(force = false) {
    // Due to some weird firebase bug(?) where the auth token times out (I think in 30m), let's avoid trying to re-fetch feeds from firebase if we already have them.
    if (!force && allColors.length > 0) {
        dlog("already fetched colors. bailing.");
        return;
    }

    try {
        dlog(`fetchColors: start; collection: ${environment.currentColorCollectionName()}`);

        const collectionRef = firebase.collection(firebaseShared.db, environment.currentColorCollectionName());
        dlog(`fetchColors: got collectionRef`);
        
        const snapshot = await firebaseShared.withRetry(() => firebase.getDocs(collectionRef));
        dlog(`fetchColors: got snapshot`);

        const tempColors = [];

        snapshot.forEach(doc => {
            tempColors.push(doc.data());
        });
        
        allColors = tempColors;
        dlog("allColors count: " + allColors.length);
    } catch (error) {
        console.warn("fetchColors failed after retries:", error);
        return;
    }

    dlog(`fetchColors: ${allColors.length} items in collection`);
}

// Color mapping utilities for device coloredProductImageIDs
function generateColorCombinationKey(colorData) {
    if (!colorData) {
        dlog("[COLOR VARIANTS] No color data provided for key generation");
        return null;
    }
    
    if (colorData.type === "single") {
        // Single color format: "none-<color-name>" (when no primary colors, only alt colors)
        const colorName = colorData.color?.name || colorData.color?.id;
        if (!colorName) {
            dlog("[COLOR VARIANTS] Missing color name for single color key generation");
            return null;
        }
        const key = `none-${colorName}`;
        dlog(`[COLOR VARIANTS] Generated single color key: ${key}`);
        return key;
    } else if (colorData.type === "combination") {
        // Combination format: "<color-name>-<altColor-name>" or "<color-name>-none"
        const primaryName = colorData.primaryColor?.name || colorData.primaryColor?.id;
        const altName = colorData.altColor?.name || colorData.altColor?.id;
        if (!primaryName || !altName) {
            dlog("[COLOR VARIANTS] Missing color names for combination key generation");
            return null;
        }
        const key = `${primaryName}-${altName}`;
        dlog(`[COLOR VARIANTS] Generated combination color key: ${key}`);
        return key;
    }
    
    dlog(`[COLOR VARIANTS] Unknown color data type: ${colorData.type}`);
    return null;
}

function generateColorKey(primaryColor, altColor) {
    if (!primaryColor) {
        return null;
    }

    if (altColor) {
        return `${primaryColor.id}-${altColor.id}`;
    } else {
        return `${primaryColor.id}-none`;
    }
}

function getColoredProductImageID(device, primaryColor, altColor) {
    if (!device) {
        dlog("[COLOR VARIANTS] No device provided for image ID lookup");
        return device?.imageID || null;
    }

    if (!primaryColor) {
        dlog("[COLOR VARIANTS] No primary color provided, using default device.imageID");
        return device.imageID;
    }

    // Check if device has coloredProductImageIDs mapping
    if (!device.coloredProductImageIDs) {
        dlog("[COLOR VARIANTS] Device has no coloredProductImageIDs mapping, using default device.imageID");
        return device.imageID;
    }

    // Generate the color combination key
    const colorKey = generateColorKey(primaryColor, altColor);

    if (!colorKey) {
        dlog("[COLOR VARIANTS] Failed to generate color key, using default device.imageID");
        return device.imageID;
    }

    // Look up the mapped image ID
    const mappedImageID = device.coloredProductImageIDs[colorKey];
    if (mappedImageID) {
        dlog(`[COLOR VARIANTS] Found mapped image ID: ${mappedImageID} for color key: ${colorKey}`);
        return mappedImageID;
    } else {
        dlog(`[COLOR VARIANTS] No mapping found for color key: ${colorKey}, using default device.imageID`);
        return device.imageID;
    }
}

function getColoredISOPreviewImageID(device, primaryColor, altColor) {
    if (!device) {
        dlog("[COLOR VARIANTS] No device provided for ISO preview image ID lookup");
        return null;
    }

    if (!primaryColor) {
        dlog("[COLOR VARIANTS] No primary color provided for ISO preview");
        return null;
    }

    // Check if device has coloredISOPreviewImageIDs mapping
    if (!device.coloredISOPreviewImageIDs) {
        dlog("[COLOR VARIANTS] Device has no coloredISOPreviewImageIDs mapping");
        return null;
    }

    // Generate the color combination key
    const colorKey = generateColorKey(primaryColor, altColor);

    if (!colorKey) {
        dlog("[COLOR VARIANTS] Failed to generate color key for ISO preview");
        return null;
    }

    // Look up the mapped ISO preview image ID
    const mappedImageID = device.coloredISOPreviewImageIDs[colorKey];
    if (mappedImageID) {
        dlog(`[COLOR VARIANTS] Found ISO preview image ID: ${mappedImageID} for color key: ${colorKey}`);
        return mappedImageID;
    } else {
        dlog(`[COLOR VARIANTS] No ISO preview mapping found for color key: ${colorKey}`);
        return null;
    }
}

function getColoredOverlayImageID(device, primaryColor, altColor) {
    // Only applies to wallet device with specific ID
    if (!device || device.id !== "C3PrebuiltMagSafeWalletPlusPopTop") {
        return null;
    }
    
    if (!altColor) {
        dlog("[COLOR VARIANTS] No alt color provided for colored overlay");
        return null;
    }
    
    // Check if device has coloredOverlays mapping
    if (!device.coloredOverlays) {
        dlog("[COLOR VARIANTS] Device has no coloredOverlays mapping");
        return null;
    }
    
    // For this specific wallet, we only use the alt color ID as the key
    const colorName = altColor.id;
    if (!colorName) {
        dlog(`[COLOR VARIANTS] Missing alt color ID for colored overlay lookup`);
        return null;
    }
    
    // Look up the mapped overlay image ID
    const mappedOverlayImageID = device.coloredOverlays[colorName];
    if (mappedOverlayImageID) {
        dlog(`[COLOR VARIANTS] Found colored overlay image ID: ${mappedOverlayImageID} for color: ${colorName}`);
        return mappedOverlayImageID;
    } else {
        dlog(`[COLOR VARIANTS] No colored overlay mapping found for color: ${colorName}`);
        return null;
    }
}

// Generate all possible color combination keys for a device
function generateAllColorCombinationKeys(device, allColorsParam = null) {
    const keys = [];
    
    // Use provided colors or fall back to the module's allColors
    const colorsToUse = allColorsParam || allColors;
    
    if (!device || !colorsToUse || colorsToUse.length === 0) {
        dlog("[COLOR VARIANTS] No device or colors provided for key generation");
        return keys;
    }
    
    // Get color objects for device.colors
    const deviceColors = device.colors ? device.colors.map(colorId => 
        colorsToUse.find(color => color.id === colorId)
    ).filter(color => color) : [];
    
    // Get color objects for device.colorsAlt
    const deviceColorsAlt = device.colorsAlt ? device.colorsAlt.map(colorId => 
        colorsToUse.find(color => color.id === colorId)
    ).filter(color => color) : [];
    
    if (deviceColors.length === 0) {
        dlog("[COLOR VARIANTS] No valid colors found for device");
        return keys;
    }
    
    if (deviceColorsAlt.length > 0) {
        // Generate combination keys (colors × colorsAlt)
        deviceColors.forEach(primaryColor => {
            deviceColorsAlt.forEach(altColor => {
                const key = `${primaryColor.id}-${altColor.id}`;
                keys.push(key);
            });
        });
        dlog(`[COLOR VARIANTS] Generated ${keys.length} combination keys for device ${device.id}`);
    } else {
        // Generate single color keys (colors with -none)
        deviceColors.forEach(color => {
            const key = `${color.id}-none`;
            keys.push(key);
        });
        dlog(`[COLOR VARIANTS] Generated ${keys.length} single color keys for device ${device.id}`);
    }
    
    return keys;
}

// Pre-load all color variant images for a device
async function preloadColorVariantImages(device, allColorsParam = null) {
    if (!device || !device.coloredProductImageIDs) {
        dlog(`[COLOR VARIANTS] Device ${device?.id || 'unknown'} has no coloredProductImageIDs, skipping pre-load`);
        return;
    }

    // Use provided colors or fall back to the module's allColors
    const colorsToUse = allColorsParam || allColors;

    const allKeys = generateAllColorCombinationKeys(device, colorsToUse);
    const imagesToPreload = [];

    // Find which keys have mapped images
    allKeys.forEach(key => {
        const imageID = device.coloredProductImageIDs[key];
        if (imageID) {
            imagesToPreload.push({ key, imageID });
        }
    });

    if (imagesToPreload.length === 0) {
        dlog(`[COLOR VARIANTS] No color variant images found for device ${device.id}`);
        return;
    }

    dlog(`[COLOR VARIANTS] Pre-loading ${imagesToPreload.length} color variant images for device ${device.id}`);

    // Pre-load images in parallel
    const maxPrdImageW = 400;
    const dpr = Math.round(window.devicePixelRatio);

    const preloadPromises = imagesToPreload.map(async ({ key, imageID }) => {
        try {
            dlog(`[COLOR VARIANTS] Pre-loading image for key: ${key}, imageID: ${imageID}`);
            await imageCache.getLoadAndStoreCF(imageID, maxPrdImageW, "webp", 85, dpr);
            dlog(`[COLOR VARIANTS] Successfully pre-loaded image for key: ${key}`);
        } catch (error) {
            dlog(`[COLOR VARIANTS] Failed to pre-load image for key: ${key}, error: ${error.message}`);
        }
    });

    try {
        await Promise.all(preloadPromises);
        dlog(`[COLOR VARIANTS] Completed pre-loading all color variant images for device ${device.id}`);
    } catch (error) {
        dlog(`[COLOR VARIANTS] Some images failed to pre-load for device ${device.id}: ${error.message}`);
    }
}

// Pre-load all ISO preview images for a device (serially to minimize CPU and network load)
async function preloadISOPreviewImages(device, allColorsParam = null) {
    if (!device || !device.coloredISOPreviewImageIDs) {
        dlog(`[COLOR VARIANTS] Device ${device?.id || 'unknown'} has no coloredISOPreviewImageIDs, skipping ISO preview pre-load`);
        return;
    }

    // Use provided colors or fall back to the module's allColors
    const colorsToUse = allColorsParam || allColors;

    const allKeys = generateAllColorCombinationKeys(device, colorsToUse);
    const imagesToPreload = [];

    // Find which keys have mapped ISO preview images
    allKeys.forEach(key => {
        const imageID = device.coloredISOPreviewImageIDs[key];
        if (imageID) {
            imagesToPreload.push({ key, imageID });
        }
    });

    if (imagesToPreload.length === 0) {
        dlog(`[COLOR VARIANTS] No ISO preview images found for device ${device.id}`);
        return;
    }

    dlog(`[COLOR VARIANTS] Pre-loading ${imagesToPreload.length} ISO preview images for device ${device.id} (serially)`);

    // Pre-load images serially to minimize CPU and network load
    const maxWidth = 400;
    const dpr = Math.round(window.devicePixelRatio);

    for (const { key, imageID } of imagesToPreload) {
        try {
            dlog(`[COLOR VARIANTS] Pre-loading ISO preview for key: ${key}, imageID: ${imageID}`);
            await imageCache.getLoadAndStoreCF(imageID, maxWidth, "webp", 85, dpr);
            dlog(`[COLOR VARIANTS] Successfully pre-loaded ISO preview for key: ${key}`);
        } catch (error) {
            dlog(`[COLOR VARIANTS] Failed to pre-load ISO preview for key: ${key}, error: ${error.message}`);
        }
    }

    dlog(`[COLOR VARIANTS] Completed pre-loading all ISO preview images for device ${device.id}`);
}

export { colorWithID, fetchColors, getAllColors, generateColorCombinationKey, generateColorKey, getColoredProductImageID, getColoredISOPreviewImageID, getColoredOverlayImageID, generateAllColorCombinationKeys, preloadColorVariantImages, preloadISOPreviewImages };

/*
Color schema for reference:

**Colors** represent the color options available for customization. Each color has a unique identifier, display name, and 8-character hex color value including alpha channel for accurate color representation across the platform.

| **Field**       | **Type** | **Description**                                              |
| --------------- | -------- | ------------------------------------------------------------ |
| `id`            | string   | Unique identifier for the color (e.g., "black1", "white1").  |
| `title`         | string   | User-visible display name of the color (e.g., "Black", "White"). |
| `hex8Color`     | string   | 8-character hex color value including alpha channel (e.g., "#000000FF"). |
| `last_modified` | string   | ISO-8601 timestamp of the last update.                       |

*/